// src/lib/guided/concernsOpened.ts
//
// Plan 5.5 of docs/guided-shopping_plan.md: which of the owner's concern
// labels a visitor actually opened before submitting.
//
// Three functions for the three places this value moves through:
//   - `withConcernOpened` -- the client accumulator, called by both
//     renderers each time a concern is expanded (never on collapse: a
//     closed concern was still opened).
//   - `sanitizeConcernsOpened` -- the server-side "never trust the client"
//     filter `saveSubmission` runs before writing
//     `Submission.metadata.concernsOpened`, the same rule submissions.ts's
//     own `gradeSubmission` already applies to the posted grade.
//   - `readStoredConcernsOpened` -- reads that stored value back for the
//     notifier and the dashboard, total over any JSON shape the same way
//     grade.ts's `readStoredLeadGrade` is.
//
// MAX_CONCERNS bounds every list here because a form can declare at most
// that many concerns in the first place (config.ts), so a visitor can never
// legitimately open more.
import { MAX_CONCERNS } from "./config";

/**
 * Add a newly opened concern's label to the accumulated list a visitor is
 * building toward submission. Deduplicated (opening the same concern twice
 * adds it once) and capped at MAX_CONCERNS.
 */
export function withConcernOpened(
  labels: readonly string[],
  label: string,
): string[] {
  if (labels.includes(label)) return labels as string[];
  if (labels.length >= MAX_CONCERNS) return labels as string[];
  return [...labels, label];
}

/**
 * Which of the posted labels are real: the form's own concern labels, never
 * a client's invention. Deduplicated and capped at MAX_CONCERNS. Returns
 * `[]` for anything that is not an array, so a malformed or absent
 * `_meta.concernsOpened` fails safe rather than throwing.
 */
export function sanitizeConcernsOpened(
  raw: unknown,
  knownLabels: readonly string[],
): string[] {
  if (!Array.isArray(raw)) return [];
  const known = new Set(knownLabels);
  const seen = new Set<string>();
  const result: string[] = [];
  for (const item of raw) {
    if (typeof item !== "string") continue;
    if (!known.has(item)) continue;
    if (seen.has(item)) continue;
    seen.add(item);
    result.push(item);
    if (result.length >= MAX_CONCERNS) break;
  }
  return result;
}

/**
 * Reads back what `sanitizeConcernsOpened` stored on
 * `Submission.metadata.concernsOpened`. Total over any shape: `metadata` is
 * a Prisma `Json?` column and most submissions (every non-guided-shopping
 * form, and every guided one predating this task) carry no such key.
 */
export function readStoredConcernsOpened(metadata: unknown): string[] {
  if (!metadata || typeof metadata !== "object") return [];
  const raw = (metadata as Record<string, unknown>).concernsOpened;
  if (!Array.isArray(raw)) return [];
  return raw.filter((label): label is string => typeof label === "string");
}
